import { useEffect, useState } from "react";
import { Download, Smartphone, X, Share } from "lucide-react";
import { toast } from "sonner";

const DISMISS_KEY = "nexus_install_dismissed_at";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
}

function isMobile() {
  if (typeof window === "undefined") return false;
  return /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
}

export default function PWAInstall() {
  const [prompt, setPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_MS) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    // Show iOS install tip after a short delay
    if (isIOS() && isMobile()) {
      const t = setTimeout(() => setShowBanner(true), 3000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      };
    }
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const install = async () => {
    if (prompt) {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome === "accepted") {
        toast.success("Nexus is installing on your device");
      }
      setPrompt(null);
      setShowBanner(false);
    } else if (isIOS()) {
      setShowIOSHelp(true);
    } else {
      toast.info("Open this site on Chrome/Edge on Android for one-tap install.");
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShowBanner(false);
    setShowIOSHelp(false);
  };

  if (isStandalone()) return null;

  return (
    <>
      {showBanner && !showIOSHelp && (
        <div
          className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50 glass rounded-2xl p-4 shadow-[0_10px_40px_rgba(0,0,0,0.5)] animate-fade-up"
          data-testid="pwa-install-banner"
        >
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl nexus-gradient flex items-center justify-center shrink-0">
              <Smartphone className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold text-sm">Install Nexus</div>
              <div className="text-xs text-zinc-400 mt-0.5">
                Add to your home screen for a one-tap app experience.
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={install}
                  className="nexus-gradient text-white text-xs font-semibold px-3 py-1.5 rounded-full hover:opacity-90 flex items-center gap-1.5"
                  data-testid="pwa-install-btn"
                >
                  <Download className="h-3 w-3" /> Install
                </button>
                <button
                  onClick={dismiss}
                  className="text-xs text-zinc-400 hover:text-white px-3 py-1.5 rounded-full"
                  data-testid="pwa-install-dismiss"
                >
                  Not now
                </button>
              </div>
            </div>
            <button
              onClick={dismiss}
              className="text-zinc-500 hover:text-white shrink-0"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {showIOSHelp && (
        <div
          className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4 bg-black/70 animate-fade-up"
          onClick={dismiss}
          data-testid="pwa-ios-help"
        >
          <div
            className="glass rounded-2xl p-6 max-w-sm w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg">Install Nexus on iPhone</div>
              <button onClick={dismiss} className="text-zinc-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="text-sm text-zinc-300 space-y-3">
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full nexus-gradient text-xs font-bold text-white flex items-center justify-center shrink-0">
                  1
                </span>
                <span>
                  Tap the <Share className="h-4 w-4 inline text-nexus-blue" /> Share icon in
                  Safari&apos;s toolbar.
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full nexus-gradient text-xs font-bold text-white flex items-center justify-center shrink-0">
                  2
                </span>
                <span>Scroll down and tap <b>Add to Home Screen</b>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-6 w-6 rounded-full nexus-gradient text-xs font-bold text-white flex items-center justify-center shrink-0">
                  3
                </span>
                <span>Tap <b>Add</b> — Nexus lands on your home screen like a native app.</span>
              </li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}

export function InstallAppButton({ className = "" }) {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    setVisible(true);
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  if (!visible) return null;

  const click = async () => {
    if (prompt) {
      prompt.prompt();
      await prompt.userChoice;
      setPrompt(null);
    } else if (isIOS()) {
      setIosHelp(true);
    } else {
      toast.info("Open in Chrome / Edge on Android to install.");
    }
  };

  return (
    <>
      <button
        onClick={click}
        className={className || "inline-flex items-center gap-2 px-4 h-11 rounded-full nexus-gradient text-white font-semibold hover:opacity-90"}
        data-testid="install-app-btn"
      >
        <Download className="h-4 w-4" /> Get the app
      </button>
      {iosHelp && (
        <div
          className="fixed inset-0 z-[60] flex items-end md:items-center justify-center p-4 bg-black/70"
          onClick={() => setIosHelp(false)}
        >
          <div className="glass rounded-2xl p-6 max-w-sm w-full space-y-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-lg">Install Nexus on iPhone</div>
              <button onClick={() => setIosHelp(false)} className="text-zinc-400 hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
            <ol className="text-sm text-zinc-300 space-y-3">
              <li>Tap the <Share className="h-4 w-4 inline text-nexus-blue" /> Share icon in Safari.</li>
              <li>Scroll and tap <b>Add to Home Screen</b>.</li>
              <li>Tap <b>Add</b> — Nexus opens like a native app.</li>
            </ol>
          </div>
        </div>
      )}
    </>
  );
}
